// ============================================================================
// CECODEV Platform - Auth Context (v2 - 2FA + rôles multiples)
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { UserSession } from "@cecodev/shared";
import { api, estAnnulation } from "../lib/api";
import { definirOrgCourante, segmentEstIdentifiant } from "../lib/urls";

/**
 * Retourne l'URL de destination après login (returnTo > /mes-seances).
 * EXPORTÉ (2026-08-18) : la création de compte doit atterrir au même endroit que
 * la connexion. Elle codait « /projets » en dur — un participant tout neuf
 * tombait donc sur « Accès non autorisé » juste après avoir créé son compte.
 * ATTERRISSAGE UNIVERSEL (FX 2026-09-19) : tous les comptes, pilotes compris,
 * s'ouvrent sur « Mon espace » (/mes-seances) — la page porte déjà l'entrée
 * pilotage (icône Tableau de bord + badge d'actions). Le pilotage (/projets)
 * reste à un clic, il n'est plus l'accueil.
 */
export function getPostLoginDestination(_userData?: any): string {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get("returnTo");
  // Sécurité : ne permettre que les chemins relatifs internes
  if (returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")) {
    return returnTo;
  }
  return "/mon-espace";
}

type LoginStep = "IDLE" | "2FA_REQUIRED" | "VERIFY_EMAIL" | "PENDING_VALIDATION" | "COMPLETE";

interface AuthContextType {
  user: UserSession | null;
  isLoading: boolean;
  // Login flow
  loginStep: LoginStep;
  pendingUserId: string | null;
  pendingEmailMasked: string | null;
  login: (email: string, password: string) => Promise<void>;
  verify2FA: (code: string, trustDevice?: boolean) => Promise<void>;
  resendCode: () => Promise<void>;
  resetLoginFlow: () => void;
  // Session
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  // Multi-org
  switchOrg: (membershipId: string) => Promise<void>;
  /** Immersion SUPER_ADMIN/pilote Cecodev dans une org non-membre (re-signe un JWT scopé). */
  immerseInOrg: (organisationId: string) => Promise<void>;
  /** Lot 9.1 : bascule vers l'org que porte l'URL (/o/:slug) — le serveur résout le slug et décide (membership ou immersion). */
  basculerVersOrg: (segment: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginStep, setLoginStep] = useState<LoginStep>("IDLE");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingEmailMasked, setPendingEmailMasked] = useState<string | null>(null);
  const navigate = useNavigate();
  // Lot 9.1 : l'org du jeton est la valeur par défaut des liens d'organisation
  // (lib/urls.ts, urlOrg) — posée à chaque rendu, avant que l'arbre ne lie.
  definirOrgCourante(user?.organisationSlug, user?.organisationId);

  // Check auth on mount
  useEffect(() => {
    const abortController = new AbortController();
    checkAuth(abortController.signal);
    return () => { abortController.abort(); };
  }, []);

  async function checkAuth(signal?: AbortSignal) {
    try {
      const response = await api.get("/auth/me", {
        signal,
        skipAuthRedirect: true,
      });
      if (!signal?.aborted) {
        setUser(response.data.user);
      }
    } catch (error) {
      if (estAnnulation(error)) return;
      if (!signal?.aborted) setUser(null);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post("/auth/login", { email, password });
    const { step, token, user: userData, userId, emailMasked } = response.data;

    if (step === "2FA_REQUIRED") {
      setLoginStep("2FA_REQUIRED");
      setPendingUserId(userId);
      setPendingEmailMasked(emailMasked);
      return;
    }

    if (step === "COMPLETE" && userData && token) {
      setUser(userData);
      localStorage.setItem("token", token);
      setLoginStep("COMPLETE");
      navigate(getPostLoginDestination(userData));
    }
  }, [navigate]);

  const verify2FA = useCallback(async (code: string, trustDevice: boolean = true) => {
    if (!pendingUserId) throw new Error("Pas de userId en attente");

    const response = await api.post("/auth/verify-2fa", {
      userId: pendingUserId,
      code,
      trustDevice,
    });

    const { step, token, user: userData, message } = response.data;

    if (step === "PENDING_VALIDATION") {
      setLoginStep("PENDING_VALIDATION");
      return;
    }

    if (step === "COMPLETE" && userData && token) {
      setUser(userData);
      localStorage.setItem("token", token);
      setLoginStep("COMPLETE");
      setPendingUserId(null);
      setPendingEmailMasked(null);
      navigate(getPostLoginDestination(userData));
    }
  }, [pendingUserId, navigate]);

  const resendCode = useCallback(async () => {
    if (!pendingUserId) return;
    await api.post("/auth/resend-code", { userId: pendingUserId });
  }, [pendingUserId]);

  const resetLoginFlow = useCallback(() => {
    setLoginStep("IDLE");
    setPendingUserId(null);
    setPendingEmailMasked(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("token");
      setLoginStep("IDLE");
      navigate("/login");
    }
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, []);

  const switchOrg = useCallback(async (membershipId: string) => {
    const response = await api.post("/auth/switch-org", { membershipId });
    const { token, user: userData } = response.data;
    if (token && userData) {
      localStorage.setItem("token", token);
      setUser(userData);
    }
  }, []);

  // Immersion : bascule sur une org par son id (membership si on en a un, sinon
  // immersion PILOTE côté serveur). Re-signe le JWT → `user.organisationId`
  // devient l'org cible pour toute la session.
  const immerseInOrg = useCallback(async (organisationId: string) => {
    const response = await api.post("/auth/switch-org", { organisationId });
    const { token, user: userData } = response.data;
    if (token && userData) {
      localStorage.setItem("token", token);
      setUser(userData);
    }
  }, []);

  // Lot 9.1 : la garde de route demande la bascule quand le segment /o/:slug
  // diffère du jeton. Un segment qui est un identifiant (org sans slug, N253)
  // part comme tel ; le serveur résout, vérifie les droits et re-signe.
  const basculerVersOrg = useCallback(async (segment: string) => {
    const corps = segmentEstIdentifiant(segment) ? { organisationId: segment } : { organisationSlug: segment };
    const response = await api.post("/auth/switch-org", corps);
    const { token, user: userData } = response.data;
    if (token && userData) {
      localStorage.setItem("token", token);
      setUser(userData);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        loginStep,
        pendingUserId,
        pendingEmailMasked,
        login,
        verify2FA,
        resendCode,
        resetLoginFlow,
        logout,
        refreshUser,
        switchOrg,
        immerseInOrg,
        basculerVersOrg,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
